import { IMAGE_TRIM } from '../constants';
import { type ImageTrimType } from '../types';
import generateMediaQuery from './generateMediaQuery';
import type { ImageNode } from 'data-layer/types';

export interface ResponsiveImageAttributes {
  src: string;
  lowSrc?: string;
  srcSet: string;
  sizes: string;
}

const FALLBACK_IMAGE_SIZE = '100vw';
const IMAGE_TRIM_AUTO_PARAMS = 'auto&trim-sd=2&trim-pad=64';
const LOW_RESOLUTION_PARAMS = 'w=200&q=30';

/**
 * Checks if a URL is an imgix URL that supports image processing parameters.
 * Imgix URLs contain 'imgix.net' in the domain.
 *
 * @param url - The image URL to check
 * @returns True if the URL supports imgix processing, false for static CDN assets
 */
function isImgixUrl(url: string): boolean {
  return url.includes('imgix.net');
}

/**
 * Generates responsive image attributes (`src`, `srcSet`, and `sizes`) for an image.
 *
 * For imgix URLs (CMS images): Applies imgix processing parameters like trim, width, quality.
 * For static CDN URLs: Returns URL as-is without processing parameters.
 *
 * @param image - The image data object.
 * @param image.url - The base URL of the image.
 * @param image.availableWidths - A list of widths for generating the `srcSet`.
 * @param trim - Determines whether automatic trimming should be applied to the image.
 * @param desktopScreenWidth - The minimum screen width in pixels to apply desktop-specific sizing.
 * @param desktopImageWidth - The width of the image to use for desktop screens (e.g., `30vw`).
 *
 * @returns An object containing `src`, `srcSet`, and `sizes` attributes.
 **/
export const getResponsiveData = (
  image: ImageNode,
  trim: ImageTrimType,
  desktopScreenWidth: number,
  desktopImageWidth: string
): ResponsiveImageAttributes => {
  // For static CDN URLs (non-imgix), return as-is without processing parameters
  if (!isImgixUrl(image.url)) {
    return {
      src: image.url,
      lowSrc: image.url,
      srcSet: `${image.url} ${image.imageTagWidth}w`,
      sizes: FALLBACK_IMAGE_SIZE
    };
  }

  // Imgix processing for CMS images
  const trimParam = trim === IMAGE_TRIM.AUTO ? IMAGE_TRIM_AUTO_PARAMS : 'false';

  const srcSet = image.availableWidths.map(size => `${image.url}&trim=${trimParam}&w=${size} ${size}w`).join(', ');

  const sizes = generateMediaQuery(desktopScreenWidth, desktopImageWidth, FALLBACK_IMAGE_SIZE);

  return {
    src: `${image.url}&trim=${trimParam}`,
    lowSrc: `${image.url}&trim=${trimParam}&${LOW_RESOLUTION_PARAMS}`,
    srcSet,
    sizes
  };
};
